import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PageSEO } from '../../components/seo/SchemaMarkup';
import { API, Eyebrow } from './locatorShared';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-zinc-400 text-xs uppercase tracking-wider">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls = 'w-full bg-zinc-900 border border-zinc-700 focus:border-[#C7FA3E] outline-none text-white text-sm px-3 py-3 placeholder:text-zinc-600';

export default function LocatorSubmit() {
  const [form, setForm] = useState({ facility_name: '', city: '', state: '', website: '', therapies: '', contact_name: '', email: '', message: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API}/api/locator/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Submission failed');
      setDone(true);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Submission failed — please retry.');
    }
  };

  return (
    <div className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid="locator-submit-page">
      <PageSEO
        title="Add Your Facility — Free Recovery Directory Listing | NinjAthlete"
        description="List your recovery studio, clinic or wellness facility free in the NinjAthlete Recovery & Peptide Therapy Locator."
        path="/locator/submit"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Recovery Locator', url: '/locator' }, { name: 'Add Your Facility', url: '/locator/submit' }]}
      />
      <div className="max-w-xl mx-auto">
        <Eyebrow>NinjAthlete Recovery Locator</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl leading-tight mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Add Your Facility — Free</h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Get in front of an audience already reading about cold plunge, peptides and recovery. Basic listings are free forever; approved facilities go live within a week and can then claim their listing to add photos, pricing and a tracked booking link.
        </p>

        {done ? (
          <div className="border border-[#C7FA3E]/30 bg-[#C7FA3E]/[0.05] p-8 text-center" data-testid="locator-submit-success">
            <CheckCircle2 className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
            <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Submission Received</h2>
            <p className="text-zinc-400 text-sm">We review every facility by hand — approved listings go live within a week and we'll email you when yours does.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Facility name *">
              <input required value={form.facility_name} onChange={set('facility_name')} className={inputCls} placeholder="Apex Recovery Lab" data-testid="submit-facility-name" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City *">
                <input required value={form.city} onChange={set('city')} className={inputCls} placeholder="Austin" data-testid="submit-city" />
              </Field>
              <Field label="State *">
                <input required value={form.state} onChange={set('state')} className={inputCls} placeholder="TX" maxLength={20} data-testid="submit-state" />
              </Field>
            </div>
            <Field label="Website">
              <input value={form.website} onChange={set('website')} className={inputCls} placeholder="https://…" data-testid="submit-website" />
            </Field>
            <Field label="Therapies offered">
              <input value={form.therapies} onChange={set('therapies')} className={inputCls} placeholder="Cold plunge, infrared sauna, IV therapy…" data-testid="submit-therapies" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Your name *">
                <input required value={form.contact_name} onChange={set('contact_name')} className={inputCls} placeholder="Jordan Smith" data-testid="submit-contact-name" />
              </Field>
              <Field label="Email *">
                <input required type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="you@facility.com" data-testid="submit-email" />
              </Field>
            </div>
            <Field label="Anything else?">
              <textarea value={form.message} onChange={set('message')} rows={3} className={inputCls} placeholder="Intro offers, medical supervision, notable equipment…" data-testid="submit-message" />
            </Field>
            <button type="submit" className="w-full py-3.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="submit-facility-btn">
              Submit Facility
            </button>
            {error && <p className="text-[#E63946] text-sm" data-testid="submit-error">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
